import {
  Box,
  Grid,
  Paper,
  Stack,
  Divider,
  useTheme,
  Typography,
} from "@mui/material";
import Image from "next/image";
import React, { useEffect, useState } from "react";

import { PerkItemCard } from "../../SelectPerks/PerkItemCard";
import { CheckoutInventoryCard } from "./CheckoutInventoryCard";

import { useAppStore } from "@/global/store";
import { findPerkParentByCode } from "../../utils.perks";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { usePerksState } from "../../SelectPerks/usePerksManager";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { convertDate, convertTime } from "@/global/utils/dateTimeConvert";
import { CartEvent } from "@/global/store/data/purchaseSeats/cartManager";
import { selectEventsSelectedById } from "@/global/store/data/purchaseSeats/events";

import type { PerkItemType } from "../../SelectPerks/perks.types";

interface CheckoutEventCardProps {
  cartEvent: CartEvent;
  renderHeaderActionItems?: () => React.ReactNode;
  hideInventory?: boolean;
  hidePerkInformation?: boolean;
}

export function CheckoutEventCard({
  cartEvent,
  renderHeaderActionItems,
  hideInventory,
  hidePerkInformation,
}: CheckoutEventCardProps) {
  const { purchaseSeatConfigs, uiConfigs, languages } = useTeamConfig();
  // Per-team override for the checkout perks section heading (defaults to
  // "Exclusive Perks"). Used by newyorkrangers_groups → "Group Deposit".
  const exclusivePerksLabel =
    languages?.purchaseSeats?.exclusivePerksLabel || "Exclusive Perks";
  const { sm: smDown } = useCssMediaQuery("down");
  const { palette } = useTheme();

  const perksState = usePerksState();
  const isOLEventCode = cartEvent.eventCode === "order-level";

  const ticketEvent = useAppStore(
    selectEventsSelectedById(cartEvent.eventCode)
  );

  const eventDateTimeReplacementEnabled = Boolean(uiConfigs?.findGames?.eventDateTimeReplacement); // prettier-ignore
  const [dateTime, setDateTime] = useState<string | null>(null);
  const inventoryCardFooterText =
    uiConfigs?.inventoryCardSettings?.inventoryCardFooterText;
  const hidePerkPrice = uiConfigs?.perks?.hidePerkPrice || false;

  useEffect(() => {
    if (eventDateTimeReplacementEnabled) {
      const replacement = uiConfigs?.findGames?.eventDateTimeReplacement?.find(
        (item) => item.eventCode === cartEvent.eventCode
      );
      if (replacement) {
        setDateTime(replacement.label);
      }
    }
  }, []);

  return (
    <Paper variant="outlined">
      {!isOLEventCode ? (
        <>
          <Stack
            p={"16px"}
            flexDirection={smDown ? "column" : "row"}
            alignItems={smDown ? "flex-start" : "center"}
            justifyContent={"space-between"}
            spacing={1}
          >
            {isOLEventCode}
            <Stack flexDirection={"row"} alignItems={"center"} gap={"16px"}>
              {ticketEvent?.logoUrl || ticketEvent?.backgroundUrl ? (
                <Image
                  src={
                    (purchaseSeatConfigs?.useBackgroundUrlForEventLogo
                      ? ticketEvent.backgroundUrl
                      : ticketEvent?.logoUrl) as string
                  }
                  alt={ticketEvent?.title}
                  width={40}
                  height={40}
                  style={{ objectFit: "contain" }}
                />
              ) : null}
              <Typography
                fontWeight={600}
                variant="subtitle2"
                color={palette.text.primary}
                lineHeight={1.1}
              >
                {ticketEvent?.title}
              </Typography>
            </Stack>

            <Box>{renderHeaderActionItems?.()}</Box>
          </Stack>
          <Divider />
          <Stack p={"16px"}>
            <Stack spacing={"8px"} direction={"row"}>
              <Typography variant="body2" color={palette.text.primary} noWrap>
                {eventDateTimeReplacementEnabled
                  ? dateTime
                  : convertDate(ticketEvent?.date)}
              </Typography>
              {eventDateTimeReplacementEnabled ? null : (
                <Divider orientation="vertical" flexItem />
              )}

              <Typography variant="body2" color={palette.text.primary} noWrap>
                {eventDateTimeReplacementEnabled
                  ? null
                  : convertTime(ticketEvent?.time)}
              </Typography>
            </Stack>
          </Stack>
          {!hidePerkInformation ? <Divider /> : null}
        </>
      ) : null}

      {isOLEventCode && !hidePerkInformation ? (
        <>
          <Stack
            p={"16px"}
            flexDirection={smDown ? "column" : "row"}
            alignItems={smDown ? "flex-start" : "center"}
            justifyContent={"space-between"}
            spacing={1}
          >
            <Stack flexDirection={"row"} alignItems={"center"} gap={"16px"}>
              <Typography
                fontWeight={600}
                variant="subtitle2"
                color={palette.text.primary}
                lineHeight={1.1}
              >
                {exclusivePerksLabel}
              </Typography>
            </Stack>
          </Stack>
          <Divider />
        </>
      ) : null}

      {!hidePerkInformation ? (
        <Box p={2}>
          <Grid container spacing={2}>
            {!hideInventory &&
              !!cartEvent?.products?.length &&
              cartEvent?.products?.map((product: any) => {
                return (
                  <Grid key={product.id} item xs={12}>
                    <CheckoutInventoryCard
                      inventory={product}
                      cartEvent={cartEvent}
                    />

                    {inventoryCardFooterText ? (
                      <Box mt={2}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontSize={"0.8rem"}
                        >
                          {inventoryCardFooterText}
                        </Typography>
                      </Box>
                    ) : null}
                  </Grid>
                );
              })}

            {cartEvent?.perks &&
              cartEvent?.perks?.length > 0 &&
              cartEvent?.perks?.map((perkItem: PerkItemType) => {
                const hideCateringPerkItem = uiConfigs?.perks?.hideCateringPerkItem; // prettier-ignore
                const hideRentalPerkItem = uiConfigs?.perks?.hideRentalPerkItem; // prettier-ignore
                const hidePremiumPerkItem = uiConfigs?.perks?.hidePremiumPerkItem; // prettier-ignore
                if (hideCateringPerkItem && perkItem.perkCode === "catering") {
                  return null;
                }
                if (hideRentalPerkItem && perkItem.perkCode === "rental") {
                  return null;
                }
                if (hidePremiumPerkItem && perkItem.perkCode === "premium") {
                  return null;
                }
                return (
                  <Grid key={perkItem.id} item xs={12}>
                    <PerkItemCard
                      perkParent={findPerkParentByCode(perksState.perkParents, perkItem.perkCode)} // prettier-ignore
                      hideParentIcon={false}
                      perkItem={perkItem}
                      hideActionItems={true}
                      hideEventDetails={true}
                      disableSelectedStyle={true}
                      hidePrice={hidePerkPrice}
                    />
                  </Grid>
                );
              })}
          </Grid>
        </Box>
      ) : null}
    </Paper>
  );
}
